import React, { useMemo } from 'react';

import {
  Stack,
  Text,
  Alert,
  SimpleGrid,
  Skeleton,
  ScrollArea,
  Group,
  Button,
  useMantineTheme,
} from '@mantine/core';
import { IconAlertCircle, IconSquareRoundedCheckFilled } from '@tabler/icons-react';
import dayjs from 'dayjs';
import useSelfBookingAppointmentAvailableSlots from 'hooks/appointmentSelfBooking/useSelfBookingAppointmentAvailableSlots';
import { SelfBookingAppointmentCoachAvailableTimeSlot } from 'types/generated';

interface TimeSlotSelectionStepProps {
  appointmentTypeId: string;
  staffId: string;
  selectedDate: Date;
  selectedTimeSlot: SelfBookingAppointmentCoachAvailableTimeSlot | null;
  athleteHasValidCredit: boolean;
  isAthleteHasValidCreditLoading: boolean;
  isAthleteHasValidCreditError: boolean;
  isAthleteHasValidCreditSuccess: boolean;
  hasSalesPortalPackages: boolean;
  isBooking: boolean;
  onSelectTimeSlot: (timeSlot: SelfBookingAppointmentCoachAvailableTimeSlot) => void;
  onBookAppointment: () => void;
}

const SelfBookingAppointmentTimeSlotSelectionStep = ({
  appointmentTypeId,
  staffId,
  selectedDate,
  selectedTimeSlot,
  athleteHasValidCredit,
  isAthleteHasValidCreditLoading,
  isAthleteHasValidCreditError,
  isAthleteHasValidCreditSuccess,
  hasSalesPortalPackages,
  isBooking,
  onSelectTimeSlot,
  onBookAppointment,
}: TimeSlotSelectionStepProps): JSX.Element => {
  const theme = useMantineTheme();

  const {
    data: availableTimeSlotsData,
    isLoading: isAvailableTimeSlotsDataLoading,
    isError: isAvailableTimeSlotsDataError,
    error: availableTimeSlotsDataError,
  } = useSelfBookingAppointmentAvailableSlots({
    appointmentTypeId,
    staffId,
    startDate: dayjs(selectedDate).format('YYYY-MM-DD'),
    enabled: isAthleteHasValidCreditSuccess && !!appointmentTypeId && !!staffId && !!selectedDate,
  });

  const filteredTimeSlots = useMemo(() => {
    const allTimeSlots = availableTimeSlotsData?.data || [];
    return allTimeSlots.filter(timeSlot => {
      if (timeSlot.disabled && timeSlot.conflictingEventType) {
        return false;
      }
      return true;
    });
  }, [availableTimeSlotsData?.data]);

  const isLoading = isAthleteHasValidCreditLoading || isAvailableTimeSlotsDataLoading;

  const TimeSlotButton = ({
    timeSlot,
  }: {
    timeSlot: SelfBookingAppointmentCoachAvailableTimeSlot;
  }): JSX.Element => {
    const isActive = selectedTimeSlot?.startDateTime === timeSlot.startDateTime;
    const isLoadingThis = isBooking && isActive;

    return (
      <Button
        variant='outline'
        color='kiloBlue'
        size='md'
        radius='md'
        loading={isLoadingThis}
        disabled={!athleteHasValidCredit}
        onClick={() => onSelectTimeSlot(timeSlot)}
        sx={{
          background: isActive ? theme.colors.kiloBlue[1] : theme.white,
          borderWidth: isActive ? '2px' : '1px',
        }}
        styles={{
          label: {
            width: '100%',
          },
        }}
      >
        <SimpleGrid cols={3} w='100%'>
          <div />
          <Group position='center'>{dayjs(timeSlot.startDateTime).format('h:mm A')}</Group>
          <Group position='right'>
            {isActive && <IconSquareRoundedCheckFilled size={20} style={{ color: theme.colors.green[5] }} />}
          </Group>
        </SimpleGrid>
      </Button>
    );
  };

  return (
    <>
      <Stack spacing={0}>
        <Text size='lg' weight={600} ta='center'>
          Select a time slot
        </Text>
        <Text ta='center' color='dimmed'>
          Below you can find the available times for the selected date
        </Text>
      </Stack>

      {isAthleteHasValidCreditError && (
        <Alert icon={<IconAlertCircle size={16} />} title='Error' color='red' mb='md'>
          Unable to check credit availability. Please try again.
        </Alert>
      )}

      {isAvailableTimeSlotsDataError && (
        <Alert icon={<IconAlertCircle size={16} />} title='Error' color='red' mb='md'>
          {availableTimeSlotsDataError?.response?.data.message ||
            'Unable to fetch availability list. Please try again.'}
        </Alert>
      )}

      {isLoading && (
        <SimpleGrid cols={1} spacing='sm'>
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} h='48px' radius='md' w='100%' />
          ))}
        </SimpleGrid>
      )}

      {!isLoading && !!filteredTimeSlots.length && (
        <ScrollArea my='xl' type='always' mih={240}>
          <SimpleGrid cols={1} spacing='sm' verticalSpacing='sm'>
            {filteredTimeSlots.map(timeSlot => (
              <TimeSlotButton key={timeSlot.startDateTime} timeSlot={timeSlot} />
            ))}
          </SimpleGrid>
        </ScrollArea>
      )}

      {!isLoading && filteredTimeSlots.length === 0 && (
        <Text ta='center' color='dimmed' mt='xl'>
          No available time slots for this date
        </Text>
      )}

      {athleteHasValidCredit && (
        <Group position='center' my='xl'>
          <Button
            size='md'
            loading={isLoading || isBooking}
            disabled={filteredTimeSlots.length === 0 || !selectedTimeSlot}
            onClick={onBookAppointment}
          >
            Book Appointment
          </Button>
        </Group>
      )}

      {!athleteHasValidCredit && (
        <Stack my='xl' spacing='md' ta='center'>
          {!hasSalesPortalPackages && (
            <>
              <Text mt='xl' size='xl' weight={600}>
                {"This Appointment Can't be Booked"}
              </Text>
              <Text size='xl'>
                {
                  "You don't have valid credits to complete this booking. Reach out to your gym or purchase a new package."
                }
              </Text>
            </>
          )}

          {hasSalesPortalPackages && (
            <Group position='center' my='xl'>
              <Button component='a' href='/packages/sales-portal' size='md'>
                Explore Packages
              </Button>
            </Group>
          )}
        </Stack>
      )}
    </>
  );
};

export default SelfBookingAppointmentTimeSlotSelectionStep;
